import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { ConfirmModal } from './ConfirmModal';
import { Input } from './Field';

interface ConfirmOpts {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}
interface AlertOpts {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
}
interface PromptOpts {
  title?: string;
  message?: ReactNode;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface Dialogs {
  /** Resolves true if confirmed, false if cancelled/dismissed. Replaces window.confirm. */
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
  /** Resolves when acknowledged. Replaces window.alert. */
  alert: (opts: AlertOpts) => Promise<void>;
  /** Resolves the trimmed value, or null if cancelled. Replaces window.prompt. */
  prompt: (opts: PromptOpts) => Promise<string | null>;
}

const DialogContext = createContext<Dialogs | null>(null);

type State =
  | { kind: 'confirm'; opts: ConfirmOpts; resolve: (v: boolean) => void }
  | { kind: 'alert'; opts: AlertOpts; resolve: () => void }
  | { kind: 'prompt'; opts: PromptOpts; resolve: (v: string | null) => void }
  | null;

/**
 * App-wide imperative dialogs: `const { confirm, alert, prompt } = useDialogs()`. Each returns a
 * promise and renders a polished in-app Modal instead of the browser's native confirm/alert/prompt.
 * One dialog at a time (these are user-triggered, one at a time). Mounted once at the app root.
 */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>(null);
  const [promptValue, setPromptValue] = useState('');

  const confirm = useCallback(
    (opts: ConfirmOpts) => new Promise<boolean>((resolve) => setState({ kind: 'confirm', opts, resolve })),
    [],
  );
  const alert = useCallback(
    (opts: AlertOpts) => new Promise<void>((resolve) => setState({ kind: 'alert', opts, resolve })),
    [],
  );
  const prompt = useCallback(
    (opts: PromptOpts) =>
      new Promise<string | null>((resolve) => {
        setPromptValue(opts.defaultValue ?? '');
        setState({ kind: 'prompt', opts, resolve });
      }),
    [],
  );

  const api = useMemo<Dialogs>(() => ({ confirm, alert, prompt }), [confirm, alert, prompt]);

  const finishConfirm = (v: boolean) => {
    if (state?.kind === 'confirm') state.resolve(v);
    setState(null);
  };
  const finishAlert = () => {
    if (state?.kind === 'alert') state.resolve();
    setState(null);
  };
  const finishPrompt = (v: string | null) => {
    if (state?.kind === 'prompt') state.resolve(v);
    setState(null);
  };

  return (
    <DialogContext.Provider value={api}>
      {children}

      {state?.kind === 'confirm' && (
        <ConfirmModal
          open
          title={state.opts.title ?? 'Are you sure?'}
          message={state.opts.message}
          confirmLabel={state.opts.confirmLabel}
          cancelLabel={state.opts.cancelLabel}
          danger={state.opts.danger}
          onConfirm={() => finishConfirm(true)}
          onClose={() => finishConfirm(false)}
        />
      )}

      {state?.kind === 'alert' && (
        <Modal
          open
          onClose={finishAlert}
          title={state.opts.title ?? 'Heads up'}
          footer={
            <Button variant="primary" onClick={finishAlert}>
              {state.opts.confirmLabel ?? 'OK'}
            </Button>
          }
        >
          <div className="text-sm text-ink-label">{state.opts.message}</div>
        </Modal>
      )}

      {state?.kind === 'prompt' && (
        <Modal
          open
          onClose={() => finishPrompt(null)}
          title={state.opts.title ?? 'Enter a value'}
          footer={
            <>
              <Button variant="secondary" onClick={() => finishPrompt(null)}>
                {state.opts.cancelLabel ?? 'Cancel'}
              </Button>
              <Button variant="primary" onClick={() => finishPrompt(promptValue.trim() || null)}>
                {state.opts.confirmLabel ?? 'Save'}
              </Button>
            </>
          }
        >
          {state.opts.message && <p className="mb-2 text-sm text-ink-label">{state.opts.message}</p>}
          <Input
            autoFocus
            value={promptValue}
            placeholder={state.opts.placeholder}
            aria-label={state.opts.label ?? 'Value'}
            onChange={(e) => setPromptValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') finishPrompt(promptValue.trim() || null);
            }}
          />
        </Modal>
      )}
    </DialogContext.Provider>
  );
}

export function useDialogs(): Dialogs {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialogs must be used within a DialogProvider');
  return ctx;
}
